import axios from 'axios';

import { handleErrors } from 'utils/api';
import {
  apiContactPitchPreviews,
  apiImportCurrentContacts,
  apiGetContactsPreview,
  apiGetContacts,
  apiDestroyAccountContacts,
  apiBulkCreateContacts,
  apiUpdateContactPitchPreviews,
  apiCreateContact,
  apiGetContact,
  apiUpdateContact,
  apiGetOutlets,
} from 'constants/apiRouting';
import { createActionType } from 'utils/redux';
import { GET_ACCOUNT_CONTACTS as GET_ACCOUNT_CONTACTS_EVENT } from 'constants/analytics';
import { analyticsTrack } from 'utils/analytics';
import { get } from 'lodash';

// objects
export const CONTACT_CREATE = createActionType('CONTACT_CREATE');
export const UPDATE_CONTACT = createActionType('UPDATE_CONTACT');
export const GET_CONTACT = createActionType('GET_CONTACT');
export const CONTACTS_CREATE = createActionType('CONTACTS_CREATE');
export const CREATE_CONTACTS_BULK = createActionType('CREATE_CONTACTS_BULK');
export const GET_PREVIEW_CONTACTS = createActionType('GET_PREVIEW_CONTACTS');
export const SET_PREVIEW_CONTACT_PARAMS = createActionType('SET_PREVIEW_CONTACT_PARAMS');
export const SEARCH_PREVIEW_CONTACTS = createActionType('SEARCH_PREVIEW_CONTACTS');
export const GET_CONTACTS = createActionType('GET_CONTACTS');
export const GET_ACCOUNT_CONTACTS = createActionType('GET_ACCOUNT_CONTACTS');
export const GET_CONTACT_PITCH_PREVIEWS = createActionType('GET_CONTACT_PITCH_PREVIEWS');
export const DELETE_CONTACT = createActionType('DELETE_CONTACT');
export const DELETE_CONTACTS = createActionType('DELETE_CONTACTS');
export const CREATE_CONTACT_PITCH_PREVIEWS = createActionType('CREATE_CONTACT_PITCH_PREVIEWS');
export const UPDATE_CONTACT_PITCH_PREVIEWS = createActionType('UPDATE_CONTACT_PITCH_PREVIEWS');
export const GET_OUTLETS = createActionType('GET_OUTLETS');
export const CONTACTS_CSV_SAVE = 'CONTACTS_CSV_SAVE';
export const RESET_CONTACTS = 'RESET_CONTACTS';
export const RESET_PREVIEW_CONTACTS = 'RESET_PREVIEW_CONTACTS';
export const SET_IS_CONTACT_CUSTOMIZED = 'SET_CONTACT_CUSTOMIZED';

export const createContact = (data, params = { sequence_id: undefined }) => (dispatch) => {
  const urlEncodedParams = new URLSearchParams(params);

  return dispatch({
    type: CONTACT_CREATE.name,
    payload: axios.post(
      apiCreateContact(),
      data,
      { withCredentials: true, params: urlEncodedParams },
    )
      .catch(error => handleErrors(error, dispatch)),
  });
};

export const getContact = (contactId, params = {}) => dispatch => (
  dispatch({
    type: GET_CONTACT.name,
    payload: axios.get(
      apiGetContact(contactId),
      { params, withCredentials: true },
    )
      .catch(error => handleErrors(error, dispatch)),
  })
);

export const getContacts = (params = {}) => dispatch => (
  dispatch({
    type: GET_CONTACTS.name,
    payload: axios.get(
      apiCreateContact(),
      { params, withCredentials: true },
    )
      .catch(error => handleErrors(error, dispatch)),
  })
);

export const updateContact = (contactId, params) => dispatch => (
  dispatch({
    type: UPDATE_CONTACT.name,
    payload: axios.patch(
      apiUpdateContact(contactId),
      params,
      { withCredentials: true },
    )
      .catch(error => handleErrors(error, dispatch)),
  })
);

export const importCurrentContacts = (params = {
  headers: null,
  reply_statuses: null,
  reply_filter_type: null,
  opportunity_stages: null,
  opportunity_filter_type: null,
  account: null,
  sequence: null,
}) => async (dispatch) => {
  await dispatch({
    type: CONTACTS_CREATE.name,
    payload: axios.post(
      apiImportCurrentContacts(),
      params, { withCredentials: true },
    ).catch((error) => {
      handleErrors(error, dispatch);
    }),
  });
};

export const resetContacts = () => (dispatch) => {
  dispatch({
    type: RESET_CONTACTS,
    payload: { results: [] },
  });
};

export const setIsContactCustomized = (sequenceContactId, isCustomized) => ({
  type: SET_IS_CONTACT_CUSTOMIZED,
  payload: {
    sequenceContactId,
    isCustomized,
  },
});

export const resetPreviewContacts = () => (dispatch) => {
  dispatch({
    type: RESET_PREVIEW_CONTACTS,
    payload: null,
  });
};

export const getAccountContacts = (
  accountId,
  params = {
    search: null,
    statuses: null,
    page: null,
    active: null,
    detail_page: null,
  },
) => async (dispatch) => {
  analyticsTrack(GET_ACCOUNT_CONTACTS_EVENT, {
    account: accountId,
    ...params,
  }, true);
  const paramsCopy = { ...params };
  const urlEncodedParams = new URLSearchParams(paramsCopy);
  await dispatch({
    type: GET_ACCOUNT_CONTACTS.name,
    payload: axios.get(
      apiGetContacts(accountId),
      { withCredentials: true, params: urlEncodedParams },
    ).catch((error) => {
      handleErrors(error, dispatch);
    }),
  });
};

export const destroyAccountContacts = (accountId, data = {}, params = {}) => async (dispatch) => {
  dispatch({
    type: DELETE_CONTACTS.pending,
    payload: get(data, 'contact_ids', []),
  });

  // eslint-disable-next-line no-undef
  analytics.track(GET_ACCOUNT_CONTACTS_EVENT, {
    account: accountId,
    ...data,
  });
  const paramsCopy = { ...params };
  const urlEncodedParams = new URLSearchParams(paramsCopy);
  const finalParams = urlEncodedParams;
  try {
    await axios.delete(
      apiDestroyAccountContacts(accountId),
      { withCredentials: true, params: finalParams, data },
    );
    dispatch({
      type: DELETE_CONTACTS.success,
      payload: get(data, 'contact_ids', []),
    });
  } catch (error) {
    dispatch({
      type: DELETE_CONTACTS.error,
      payload: get(data, 'contact_ids', []),
    });
    handleErrors(error, dispatch);
  }
};

export const getContactPitchesPreview = sequenceContactId => async (dispatch) => {
  await dispatch({
    type: GET_CONTACT_PITCH_PREVIEWS.name,
    payload: axios.get(
      apiContactPitchPreviews(sequenceContactId),
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const createContactPitchesPreview = (sequenceContactId, data = []) => async (dispatch) => {
  await dispatch({
    type: CREATE_CONTACT_PITCH_PREVIEWS.name,
    payload: axios.post(
      apiUpdateContactPitchPreviews(sequenceContactId),
      data,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
  dispatch(setIsContactCustomized(sequenceContactId, true));
};

export const updateContactPitchesPreview = (sequenceContactId, data = []) => async (dispatch) => {
  await dispatch({
    type: UPDATE_CONTACT_PITCH_PREVIEWS.name,
    payload: axios.put(
      apiUpdateContactPitchPreviews(sequenceContactId),
      data,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const deleteCustomizedPitch = (sequenceContactId, pitchId) => async (dispatch) => {
  await dispatch({
    payload: axios.delete(
      apiUpdateContactPitchPreviews(sequenceContactId),
      { withCredentials: true, data: { id: pitchId } },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const deleteContact = contactId => (dispatch) => {
  dispatch({
    type: DELETE_CONTACT.success,
    payload: contactId,
  });
};

export const saveContactsCSV = contacts => (dispatch) => {
  dispatch({
    type: CONTACTS_CSV_SAVE,
    payload: contacts,
  });
};

export const createContactsBulk = (data = [], params = { sequence_id: undefined }) => async (dispatch) => {
  const urlEncodedParams = new URLSearchParams(params);
  return dispatch({
    type: CREATE_CONTACTS_BULK.name,
    payload: axios.post(
      apiBulkCreateContacts(),
      data,
      { withCredentials: true, params: urlEncodedParams },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const getPreviewContacts = (data = {}) => async (dispatch) => {
  await dispatch({
    type: GET_PREVIEW_CONTACTS.name,
    payload: axios.post(
      apiGetContactsPreview(),
      data,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const setPreviewContactsParams = (params = {}) => async (dispatch) => {
  await dispatch({
    type: SET_PREVIEW_CONTACT_PARAMS.success,
    payload: { params },
  });
};

export const searchPreviewContacts = (previewParams = {}) => async (dispatch) => {
  await dispatch({
    type: SEARCH_PREVIEW_CONTACTS.success,
    payload: { ...previewParams },
  });
};


export const getOutlets = (params = {
  search: null,
  status: null,
  account__id: null,
  page: null,
  url: null,
}) => async (dispatch) => {
  await dispatch({
    type: GET_OUTLETS.name,
    payload: axios.get(
      params.url || apiGetOutlets(),
      params.url ? { withCredentials: true } : { params, withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};
